import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { useAuth } from "../../context/AuthContext";
import { normalizeImageForUpload } from "../../lib/imageFile";
import { updateUserProfile, changePassword, updateAthleteProfile, getParentChildren, updateParentChild, type ParentChild } from "../../services/api";
import { uploadProfileImage } from "../../services/users";
import { GameButton, GameCard, GameHero, GameIcon } from "../../components/game";
import { ONBOARDING_FLAG } from "../../components/OnboardingTour";
import DateInput from "../../components/DateInput";
import ChildSettingsEditor from "./parent/ChildSettingsEditor";

export default function AppSettings() {
  const { t, i18n } = useTranslation();
  const { user, athleteProfile, refresh, activeRole, logout } = useAuth();
  // Per-device preference. Persisted so the choice survives reloads instead of
  // silently resetting to on.
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem("igrowth_notifications_enabled") !== "0"; } catch { return true; }
  });
  const toggleNotifications = () => {
    setNotificationsEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem("igrowth_notifications_enabled", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State for parent's children
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);

  // Profile form state
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    date_of_birth: "",
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  // Guardian info state (for minors)
  const [guardianForm, setGuardianForm] = useState({
    name: "",
    email: "",
    phone: "",
    relationship: "",
  });

  // Calculate if user is a minor (under 14)
  const isMinor = () => {
    if (!user?.date_of_birth) return false;
    const dob = new Date(user.date_of_birth);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      return age - 1 < 14;
    }
    return age < 14;
  };

  // Initialize form values
  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || "",
        phone: user.phone || "",
        date_of_birth: user.date_of_birth ? String(user.date_of_birth).slice(0, 10) : "",
      });
    }
    if (athleteProfile?.guardian_contact) {
      setGuardianForm({
        name: athleteProfile.guardian_contact.name || "",
        email: athleteProfile.guardian_contact.email || "",
        phone: athleteProfile.guardian_contact.phone || "",
        relationship: athleteProfile.guardian_contact.relationship || "",
      });
    }
  }, [user, athleteProfile]);

  // Load children data for parents
  useEffect(() => {
    if (activeRole === "parent") {
      setLoadingChildren(true);
      getParentChildren()
        .then(data => setChildren(data))
        .catch(err => console.error("Failed to load children:", err))
        .finally(() => setLoadingChildren(false));
    }
  }, [activeRole]);

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (!picked) return;

    // No client-side type gate. Android's picker often reports no MIME type at
    // all (or application/octet-stream) for an ordinary photo, and rejecting on
    // that string is what blocked uploads there while iOS worked. The file is
    // normalised below and the server validates the real bytes.

    try {
      setUploadingImage(true);
      setError("");
      // Decodes and re-encodes the photo: converts HEIC/HEIF off an Android
      // camera into JPEG and shrinks a 12MP shot under the 5MB server cap.
      const file = await normalizeImageForUpload(picked);
      if (file.size > 5 * 1024 * 1024) {
        setError(t("profile.imageTooLarge", "Imaginea este prea mare. Maxim 5MB."));
        return;
      }
      const { url } = await uploadProfileImage(file);
      await updateUserProfile({ profile_url: url });
      await refresh();
      setSuccess(t("profile.imageUploaded", "Imaginea de profil a fost actualizata!"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      console.error("Failed to upload image:", err);
      setError(err.response?.data?.error || t("profile.imageUploadError", "Nu s-a putut incarca imaginea"));
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await updateUserProfile({
        full_name: form.full_name,
        phone: form.phone,
        date_of_birth: form.date_of_birth || null,
      });

      // If minor and guardian info provided, save it
      if (isMinor()) {
        await updateAthleteProfile({
          guardian_contact: {
            name: guardianForm.name,
            email: guardianForm.email,
            phone: guardianForm.phone,
            relationship: guardianForm.relationship,
          },
          has_guardian: true,
        });
      }

      await refresh();
      setSuccess(t("profile.saved", "Profilul a fost salvat!"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      console.error("Failed to save profile:", err);
      setError(err.response?.data?.error || t("profile.saveError", "Nu s-a putut salva profilul"));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError(t("profile.passwordMismatch", "Parolele nu se potrivesc"));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError(t("profile.passwordTooShort", "Parola trebuie sa aiba minim 6 caractere"));
      return;
    }

    setSaving(true);
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setSuccess(t("profile.passwordChanged", "Parola a fost schimbată!"));
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setShowPasswordSection(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      console.error("Failed to change password:", err);
      setError(err.response?.data?.error || t("profile.passwordError", "Nu s-a putut schimbă parola"));
    } finally {
      setSaving(false);
    }
  };

  const profileUrl = user?.profile_url || user?.avatar_url;

  const inputClass =
    "w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";
  const guardianInputClass =
    "w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500";

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-5">
        {/* Hero header — matches the flow hero used across the app */}
        <GameHero flow className="-mx-4 -mt-6 rounded-br-3xl px-4 pt-3 pb-4">
          <h1 className="text-xl font-extrabold">{t("settings.title", "Setări")}</h1>
          <p className="text-white/70 text-[13px] mt-0.5">{t("settings.subtitle", "Personalizează experiența ta")}</p>
        </GameHero>

        {/* Status Messages */}
        {error && (
          <div className="p-3 bg-red-50 text-red-600 rounded-2xl text-sm font-semibold">{error}</div>
        )}
        {success && (
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl text-sm font-semibold flex items-center gap-2">
            <GameIcon name="check" className="w-5 h-5" />
            {success}
          </div>
        )}

        {/* Profile Section with Image Upload */}
        <GameCard className="overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 border-slate-100">
            <h2 className="text-sm font-extrabold tracking-wide uppercase text-slate-600">
              {t("settings.profile", "Profil")}
            </h2>
          </div>
          <div className="p-4">
            {/* Profile Image with Upload */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative w-24 h-24 group">
                {profileUrl ? (
                  <img
                    src={profileUrl}
                    alt={user?.full_name || "Avatar"}
                    onClick={() => !uploadingImage && fileInputRef.current?.click()}
                    className="w-24 h-24 rounded-full object-cover shadow-lg border-4 border-white cursor-pointer transition-opacity hover:opacity-90"
                  />
                ) : (
                  <div
                    onClick={() => !uploadingImage && fileInputRef.current?.click()}
                    className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white text-2xl font-extrabold shadow-lg border-4 border-white cursor-pointer transition-opacity hover:opacity-90"
                  >
                    {(user?.full_name || "U").charAt(0).toUpperCase()}
                  </div>
                )}
                {/* Upload Button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="absolute -bottom-1 -right-1 w-9 h-9 bg-indigo-500 rounded-full flex items-center justify-center shadow-lg hover:bg-indigo-600 transition-colors active:translate-y-[2px]"
                >
                  {uploadingImage ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">{t("profile.clickToChange", "Apasă pentru a schimbă poza")}</p>
            </div>

            {/* Profile Form */}
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">
                  {t("profile.fullName", "Nume complet")}
                </label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={e => setForm({ ...form, full_name: e.target.value })}
                  className={inputClass}
                  placeholder={t("profile.fullNamePlaceholder", "ex: Ion Popescu")}
                />
              </div>

              {/* Phone + Date of birth (side by side) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">
                    {t("profile.phone", "Telefon")}
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    className={inputClass}
                    placeholder={t("profile.phonePlaceholder", "ex: 0712 345 678")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">
                    {t("profile.dateOfBirth", "Data nașterii")}
                  </label>
                  <DateInput
                    value={form.date_of_birth}
                    onChange={(iso) => setForm({ ...form, date_of_birth: iso })}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">
                  {t("profile.email", "Email")}
                </label>
                <input
                  type="email"
                  value={user?.email || ""}
                  disabled
                  className="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-2xl text-slate-500 cursor-not-allowed"
                />
                <p className="text-xs text-slate-400 mt-1">{t("profile.emailCantChange", "Emailul nu poate fi modificat")}</p>
              </div>

              {/* Submit Button */}
              <GameButton type="submit" variant="primary" disabled={saving}>
                {saving ? t("common.saving", "Se salvează...") : t("profile.save", "Salvează modificările")}
              </GameButton>
            </form>
          </div>
        </GameCard>

        {/* Children Section (for parents only) */}
        {activeRole === "parent" && (
          <GameCard className="overflow-hidden">
            <div className="px-4 py-3 border-b bg-indigo-50 border-indigo-100">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <h2 className="text-sm font-extrabold tracking-wide uppercase text-indigo-700">
                  {t("settings.children", "Sportivii mei")}
                </h2>
              </div>
            </div>
            <div className="p-4">
              {loadingChildren ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : children.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  {t("settings.noChildren", "Nu aveți copii înregistrați în sistem.")}
                </p>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500 -mt-1">
                    {t("settings.childrenHint", "Modifică datele fiecărui copil: poză, nume, dată de naștere, email și parolă.")}
                  </p>
                  {children.map(child => (
                    <ChildSettingsEditor
                      key={child.athlete_id}
                      child={child}
                      onSaved={() => { getParentChildren().then(setChildren).catch(() => {}); }}
                    />
                  ))}
                </div>
              )}
            </div>
          </GameCard>
        )}

        {/* Guardian Info Section (for minors only) */}
        {isMinor() && (
          <GameCard className="overflow-hidden">
            <div className="px-4 py-3 border-b bg-amber-50 border-amber-100">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h2 className="text-sm font-extrabold tracking-wide uppercase text-amber-700">
                  {t("settings.guardianInfo", "Date părinte / tutore")}
                </h2>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-slate-600">
                {t("settings.guardianNote", "Deoarece ești minor, părintele sau tutorele tău poate fi contactat de club.")}
              </p>

              {/* Guardian Name */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">
                  {t("guardian.name", "Nume părinte")}
                </label>
                <input
                  type="text"
                  value={guardianForm.name}
                  onChange={e => setGuardianForm({ ...guardianForm, name: e.target.value })}
                  className={guardianInputClass}
                  placeholder={t("guardian.namePlaceholder", "ex: Maria Popescu")}
                />
              </div>

              {/* Guardian Email */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">
                  {t("guardian.email", "Email părinte")}
                </label>
                <input
                  type="email"
                  value={guardianForm.email}
                  onChange={e => setGuardianForm({ ...guardianForm, email: e.target.value })}
                  className={guardianInputClass}
                  placeholder={t("guardian.emailPlaceholder", "ex: părinte@email.com")}
                />
              </div>

              {/* Guardian Phone */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">
                  {t("guardian.phone", "Telefon părinte")}
                </label>
                <input
                  type="tel"
                  value={guardianForm.phone}
                  onChange={e => setGuardianForm({ ...guardianForm, phone: e.target.value })}
                  className={guardianInputClass}
                  placeholder={t("guardian.phonePlaceholder", "ex: 0712 345 678")}
                />
              </div>

              {/* Guardian Relationship */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">
                  {t("guardian.relationship", "Relație")}
                </label>
                <select
                  value={guardianForm.relationship}
                  onChange={e => setGuardianForm({ ...guardianForm, relationship: e.target.value })}
                  className={guardianInputClass}
                >
                  <option value="">{t("guardian.selectRelationship", "Selectează...")}</option>
                  <option value="mother">{t("guardian.mother", "Mamă")}</option>
                  <option value="father">{t("guardian.father", "Tată")}</option>
                  <option value="legal_guardian">{t("guardian.legalGuardian", "Tutore legal")}</option>
                  <option value="other">{t("guardian.other", "Altul")}</option>
                </select>
              </div>
            </div>
          </GameCard>
        )}

        {/* Password Change Section */}
        <GameCard className="overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 border-slate-100">
            <h2 className="text-sm font-extrabold tracking-wide uppercase text-slate-600">
              {t("settings.security", "Securitate")}
            </h2>
          </div>
          <div className="p-4">
            {!showPasswordSection ? (
              <GameButton
                variant="ghost"
                onClick={() => setShowPasswordSection(true)}
                className="justify-between px-3"
              >
                <span className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                    <GameIcon name="lock" className="w-5 h-5" />
                  </span>
                  <span className="font-bold text-slate-700">{t("profile.changePassword", "Schimbă parola")}</span>
                </span>
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </GameButton>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">
                    {t("profile.currentPassword", "Parola curentă")}
                  </label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className={inputClass}
                    required
                  />
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">
                    {t("profile.newPassword", "Parola nouă")}
                  </label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className={inputClass}
                    minLength={6}
                    required
                  />
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">
                    {t("profile.confirmPassword", "Confirmă parola nouă")}
                  </label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className={inputClass}
                    minLength={6}
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <GameButton
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                    }}
                  >
                    {t("common.cancel", "Anulează")}
                  </GameButton>
                  <GameButton type="submit" variant="primary" disabled={saving}>
                    {saving ? t("common.saving", "Se salvează...") : t("profile.changePassword", "Schimbă parola")}
                  </GameButton>
                </div>
              </form>
            )}
          </div>
        </GameCard>

        {/* Language Section */}
        <GameCard className="overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 border-slate-100">
            <h2 className="text-sm font-extrabold tracking-wide uppercase text-slate-600">
              {t("settings.language", "Limba")}
            </h2>
          </div>
          <div className="p-4 space-y-2">
            <button
              onClick={() => handleLanguageChange("ro")}
              className={`w-full flex items-center justify-between p-3 rounded-2xl transition-colors active:translate-y-[1px] ${
                i18n.language === "ro"
                  ? "bg-indigo-50 border-2 border-indigo-500"
                  : "bg-slate-50 border-2 border-transparent"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">🇷🇴</span>
                <span className={`font-bold ${i18n.language === "ro" ? "text-indigo-700" : "text-slate-700"}`}>
                  Română
                </span>
              </div>
              {i18n.language === "ro" && (
                <span className="text-indigo-600">
                  <GameIcon name="check" className="w-5 h-5" />
                </span>
              )}
            </button>
            <button
              onClick={() => handleLanguageChange("en")}
              className={`w-full flex items-center justify-between p-3 rounded-2xl transition-colors active:translate-y-[1px] ${
                i18n.language === "en"
                  ? "bg-indigo-50 border-2 border-indigo-500"
                  : "bg-slate-50 border-2 border-transparent"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">🇬🇧</span>
                <span className={`font-bold ${i18n.language === "en" ? "text-indigo-700" : "text-slate-700"}`}>
                  English
                </span>
              </div>
              {i18n.language === "en" && (
                <span className="text-indigo-600">
                  <GameIcon name="check" className="w-5 h-5" />
                </span>
              )}
            </button>
          </div>
        </GameCard>

        {/* Notifications Section */}
        <GameCard className="overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 border-slate-100">
            <h2 className="text-sm font-extrabold tracking-wide uppercase text-slate-600">
              {t("settings.notifications", "Notificări")}
            </h2>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-slate-800">{t("settings.pushNotifications", "Notificări push")}</p>
                <p className="text-sm text-slate-500">{t("settings.pushDesc", "Primește notificări despre sesiuni și puncte")}</p>
              </div>
              <button
                onClick={toggleNotifications}
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  notificationsEnabled ? "bg-indigo-600" : "bg-slate-300"
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    notificationsEnabled ? "right-1" : "left-1"
                  }`}
                />
              </button>
            </div>
          </div>
        </GameCard>

        {/* App Info */}
        <GameCard className="overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 border-slate-100">
            <h2 className="text-sm font-extrabold tracking-wide uppercase text-slate-600">
              {t("settings.about", "Despre aplicație")}
            </h2>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">{t("settings.version", "Versiune")}</span>
              <span className="font-bold text-slate-800">1.0.0</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">{t("settings.platform", "Platformă")}</span>
              <span className="font-bold text-slate-800">iGROWth</span>
            </div>
            {activeRole !== "coach" && activeRole !== "parent" && (
              <button
                onClick={() => {
                  try {
                    localStorage.removeItem(ONBOARDING_FLAG);
                  } catch {
                    /* ignore */
                  }
                  window.location.href = "/app/dashboard";
                }}
                className="w-full flex items-center justify-between pt-3 border-t border-slate-100 text-indigo-600 font-bold active:translate-y-[1px] transition"
              >
                <span className="flex items-center gap-2">
                  <GameIcon name="bulb" className="w-5 h-5" />
                  {t("settings.replayTour", "Revezi tutorialul")}
                </span>
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </GameCard>

        {/* Logout */}
        <GameButton
          variant="danger"
          onClick={() => {
            logout();
            window.location.href = "/login";
          }}
          className="!bg-gradient-to-b !from-rose-50 !to-rose-100 !text-rose-600 !shadow-[0_3px_0_#fecaca]"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          {t("settings.logout", "Delogare")}
        </GameButton>
      </div>
    </MobileAppLayout>
  );
}
